import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { CostPaymentsWeek, CostTermsGroup } from '../../../lib/api'
import { useFormatters } from '../../../contexts/PreferencesContext'
import { useAxisAmount } from '../charts/useAxisAmount'
import { termsDataKey } from './termsPalette'

interface PaymentsDueChartProps {
  weeks: CostPaymentsWeek[]
  groups: CostTermsGroup[]
  colours: Record<string, string>
  onPick: (week: CostPaymentsWeek, terms: string) => void
  /** Legend show/hide (CR-1421 item 5), keyed by legend label. */
  isHidden?: (label: string) => boolean
}

type Row = CostPaymentsWeek & Record<string, number | string | boolean | null | object>

interface TipProps {
  active?: boolean
  payload?: Array<{ payload?: Row }>
  groups: CostTermsGroup[]
  colours: Record<string, string>
}

function WeekTip({ active, payload, groups, colours }: TipProps) {
  const { formatCurrency } = useFormatters()
  const row = payload?.[0]?.payload
  if (!active || !row) return null
  return (
    <div className="min-w-[200px] rounded-md border bg-white px-3 py-2 text-xs shadow-md">
      <div className={`mb-1 font-semibold ${row.overdue ? 'text-red-600' : ''}`}>{row.label}</div>
      {groups.filter((g) => row.terms[g.key]).map((g) => (
        <div key={g.key} className="flex justify-between gap-4 text-slate-600">
          <span className="flex items-center gap-1.5">
            <i className="inline-block h-2 w-2 rounded-sm" style={{ background: colours[g.key] }} />{g.label}
          </span>
          <span className="tabular-nums">{formatCurrency(row.terms[g.key].value)}</span>
        </div>
      ))}
      <div className="mt-1 flex justify-between gap-4 border-t pt-1 font-medium">
        <span>Payments due</span><span className="tabular-nums">{formatCurrency(row.value)}</span>
      </div>
    </div>
  )
}

/**
 * CR-1421: "Payments Due per Week by Expected Payment Date" (new).
 *
 * Open line value by the week its payment falls due, stacked by payment
 * terms; payments already past due sit in the Overdue bar on the first page.
 */
export function PaymentsDueChart({ weeks, groups, colours, onPick, isHidden = () => false }: PaymentsDueChartProps) {
  const axisAmount = useAxisAmount() // CR-7120: numbers only on the axis
  const rows = useMemo<Row[]>(() => weeks.map((w) => {
    const row: Row = { ...w }
    for (const g of groups) row[termsDataKey(g.key)] = w.terms[g.key]?.value ?? 0
    return row
  }), [weeks, groups])

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={rows} style={{ cursor: 'pointer' }} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis
          dataKey="label" interval={0} angle={-30} textAnchor="end" height={52}
          tick={({ x, y, payload }: { x: number; y: number; payload: { value: string } }) => (
            <text x={x} y={y + 4} textAnchor="end" fontSize={11} transform={`rotate(-30 ${x} ${y + 4})`}
              fill={payload.value === 'Overdue' ? '#dc2626' : '#64748b'}>{payload.value}</text>
          )}
        />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={axisAmount} />
        <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.08)' }} content={<WeekTip groups={groups} colours={colours} />} />
        {groups.map((g, i) => (
          <Bar key={g.key} stackId="due" dataKey={termsDataKey(g.key)} name={g.label} fill={colours[g.key]} hide={isHidden(g.label)}
            radius={i === groups.length - 1 ? [4, 4, 0, 0] : undefined}
            onClick={(data: { payload?: Row }) => { if (data?.payload) onPick(data.payload, g.key) }} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}
